import type {
  AiRankingPayload,
  Broker,
  CampaignRow,
  CompanyRegsPayload,
  ConversionsPayload,
  DatePreset,
  DateRange,
  MsAdsCampaignsPayload,
  MsAdsAdGroupsPayload,
  NewsDigest,
  NewsItem,
  NewsTopic,
  OpenaiCampaignsPayload,
  OverviewPayload,
  RoboforexPayload, RoboforexKampagnenPayload, GoogleCampaignsPayload, MetaCampaignsPayload, RedditCampaignsPayload, MgidCampaignsPayload, TaboolaCampaignsPayload, KapitalPayload, KampagnenPayload, OnexbetPayload, LandingsPayload,
  WebsitesPayload,
  WebsitesAbtestPayload,
  WebsitesCustomersPayload,
} from "@dashboard/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `${path} failed: ${res.status}`);
  return data;
}

/** Zeitraum als Query — dieselbe Form wie bei den MS-Ads-Routen. */
type RangeParams = { preset?: DatePreset; from?: string; to?: string };

function rangeQuery(r: RangeParams): string {
  const qs = new URLSearchParams();
  if (r.from && r.to) {
    qs.set("from", r.from);
    qs.set("to", r.to);
  } else if (r.preset) {
    qs.set("preset", r.preset);
  }
  return qs.toString();
}

export const api = {
  overview: (range: DateRange) => get<OverviewPayload>(`/api/overview?range=${range}`),
  brokerDetail: (id: Broker, range: DateRange) =>
    get<{ timeseries: { date: string; spend: number; regs: number }[] }>(
      `/api/broker/${id}?range=${range}`,
    ),
  campaigns: (id: Broker, range: DateRange) =>
    get<{ rows: CampaignRow[] }>(`/api/campaigns/${id}?range=${range}`),
  newsTopics: () => get<{ topics: NewsTopic[] }>(`/api/news/topics`),
  newsDigest: () => get<NewsDigest>(`/api/news/digest`),
  newsItems: (topic: string) => get<{ items: NewsItem[] }>(`/api/news/${topic}`),
  aiRanking: () => get<AiRankingPayload>(`/api/ai/ranking`),
  msadsCampaigns: (r: { preset?: DatePreset; from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (r.from && r.to) { qs.set("from", r.from); qs.set("to", r.to); }
    else if (r.preset) qs.set("preset", r.preset);
    return get<MsAdsCampaignsPayload>(`/api/msads/campaigns?${qs}`);
  },
  /** Anzeigengruppen einer Microsoft-Kampagne (eToro-FTD-Kampagne, Operator 2026-10-20). */
  msadsAdGroups: (r: { preset?: DatePreset; from?: string; to?: string; accountId: string; campaignId: string }) => {
    const qs = new URLSearchParams({ accountId: r.accountId, campaignId: r.campaignId });
    if (r.from && r.to) { qs.set("from", r.from); qs.set("to", r.to); }
    else if (r.preset) qs.set("preset", r.preset);
    return get<MsAdsAdGroupsPayload>(`/api/msads/adgroups?${qs}`);
  },
  companies: (r: { preset?: DatePreset; from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (r.from && r.to) { qs.set("from", r.from); qs.set("to", r.to); }
    else if (r.preset) qs.set("preset", r.preset);
    return get<CompanyRegsPayload>(`/api/companies?${qs}`);
  },
  conversions: (r: RangeParams) =>
    get<ConversionsPayload>(`/api/conversions?${rangeQuery(r)}`),
  openaiCampaigns: (r: RangeParams) =>
    get<OpenaiCampaignsPayload>(`/api/openai/campaigns?${rangeQuery(r)}`),
  websites: (r: RangeParams = {}) => get<WebsitesPayload>(`/api/websites?${rangeQuery(r)}`),
  websitesAbtest: (r: RangeParams = {}) =>
    get<WebsitesAbtestPayload>(`/api/websites/abtest?${rangeQuery(r)}`),
  websitesCustomers: (r: RangeParams = {}) =>
    get<WebsitesCustomersPayload>(`/api/websites/customers?${rangeQuery(r)}`),
  websitesAction: (
    action: "send" | "discard" | "generate" | "classify",
    body: Record<string, unknown>,
  ) =>
    post<{ ok: boolean; pending?: boolean; error: string | null }>(`/api/websites/${action}`, body),
  roboforex: (r: { preset?: DatePreset; from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (r.from && r.to) { qs.set("from", r.from); qs.set("to", r.to); }
    else if (r.preset) qs.set("preset", r.preset);
    return get<RoboforexPayload>(`/api/roboforex?${qs}`);
  },
  /** Kapital mit Zugangscode; wirft Error("pin") bei falschem/fehlendem Code, Error("gesperrt") nach zu vielen Versuchen. */
  kapital: async (pin: string): Promise<KapitalPayload> => {
    const res = await fetch(`${API_BASE}/api/kapital`, { headers: { "x-kapital-pin": pin } });
    if (res.status === 401) throw new Error("pin");
    if (res.status === 429) throw new Error("gesperrt");
    if (!res.ok) throw new Error(`/api/kapital failed: ${res.status}`);
    return res.json() as Promise<KapitalPayload>;
  },
  googleCampaigns: (r: RangeParams, konto?: string) =>
    get<GoogleCampaignsPayload>(`/api/google/campaigns?${rangeQuery(r)}${konto ? `&konto=${encodeURIComponent(konto)}` : ""}`),
  metaCampaigns: (r: RangeParams) =>
    get<MetaCampaignsPayload>(`/api/meta/campaigns?${rangeQuery(r)}`),
  redditCampaigns: (r: RangeParams) =>
    get<RedditCampaignsPayload>(`/api/reddit/campaigns?${rangeQuery(r)}`),
  mgidCampaigns: (r: RangeParams) =>
    get<MgidCampaignsPayload>(`/api/mgid/campaigns?${rangeQuery(r)}`),
  taboolaCampaigns: (r: RangeParams) =>
    get<TaboolaCampaignsPayload>(`/api/taboola/campaigns?${rangeQuery(r)}`),
  roboforexKampagnen: (r: RangeParams) =>
    get<RoboforexKampagnenPayload>(`/api/roboforex/kampagnen?${rangeQuery(r)}`),
  kampagnen: (broker: string, r: RangeParams) =>
    get<KampagnenPayload>(`/api/kampagnen?broker=${encodeURIComponent(broker)}&${rangeQuery(r)}`),
  onexbet: (r: RangeParams) =>
    get<OnexbetPayload>(`/api/onexbet?${rangeQuery(r)}`),
  landings: (r: RangeParams) =>
    get<LandingsPayload>(`/api/landings?${rangeQuery(r)}`),
};
